/**
 * ActionQueue — Pending actionable notifications page
 * Phase 4.7.5 (WQ-170)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getActionQueue, completeAction, type Notification } from '../../services/notificationService';

const CATEGORY_COLORS: Record<string, string> = {
  approval: '#9333ea',
  review: '#3b82f6',
  alert: '#ea580c',
  informational: '#6b7280',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#eab308',
  low: '#3b82f6',
  info: '#6b7280',
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const ActionQueue: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState<string>('');

  const fetchQueue = useCallback(async () => {
    try {
      const data = await getActionQueue(authFetch);
      setItems(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => {
    if (!authLoading) fetchQueue();
  }, [authLoading, fetchQueue]);

  const handleComplete = async (id: string, outcome?: string) => {
    try {
      await completeAction(authFetch, id, outcome);
      setItems((prev) => prev.filter((n) => n.id !== id));
    } catch { /* silent */ }
  };

  const filtered = filterCategory ? items.filter((n) => n.category === filterCategory) : items;

  const counts = {
    total: items.length,
    approval: items.filter((n) => n.category === 'approval').length,
    review: items.filter((n) => n.category === 'review').length,
    alert: items.filter((n) => n.category === 'alert').length,
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 'var(--space-6)' }}>
        <h1 className="page-title">Action Queue</h1>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        {[
          { label: 'Total Pending', count: counts.total, color: '#3b82f6' },
          { label: 'Approvals', count: counts.approval, color: CATEGORY_COLORS.approval },
          { label: 'Reviews', count: counts.review, color: CATEGORY_COLORS.review },
          { label: 'Alerts', count: counts.alert, color: CATEGORY_COLORS.alert },
        ].map((card) => (
          <div
            key={card.label}
            className="card"
            style={{
              padding: 'var(--space-4)',
              textAlign: 'center',
              borderTop: `3px solid ${card.color}`,
            }}
          >
            <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)' }}>{card.count}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ marginBottom: 'var(--space-4)', display: 'flex', gap: '8px' }}>
        {['', 'approval', 'review', 'alert'].map((cat) => (
          <button
            key={cat}
            onClick={() => setFilterCategory(cat)}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: filterCategory === cat ? '1px solid var(--accent-primary)' : '1px solid var(--glass-border)',
              background: filterCategory === cat ? 'rgba(59,130,246,0.15)' : 'var(--glass-bg)',
              color: filterCategory === cat ? 'var(--accent-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            {cat || 'All'}
          </button>
        ))}
      </div>

      {/* Action list */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', marginBottom: '12px', color: 'var(--text-muted)' }}>&#10003;</div>
          <div style={{ color: 'var(--text-muted)' }}>No actions pending. All caught up.</div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                {['Category', 'Title', 'Severity', 'Age', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((n) => (
                <tr key={n.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                      background: `${CATEGORY_COLORS[n.category] || '#6b7280'}22`,
                      color: CATEGORY_COLORS[n.category] || '#6b7280',
                    }}>
                      {n.category}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{n.title}</div>
                    {n.entity_type && (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {n.entity_type} {n.entity_id ? `(${n.entity_id.slice(0, 8)}...)` : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                      background: `${SEVERITY_COLORS[n.severity] || '#6b7280'}22`,
                      color: SEVERITY_COLORS[n.severity] || '#6b7280',
                    }}>
                      {n.severity}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                    {relativeTime(n.created_at)}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {n.action_type === 'approve_reject' ? (
                        <>
                          <button onClick={() => handleComplete(n.id, 'approved')} style={actionBtnStyle('#22c55e')}>Approve</button>
                          <button onClick={() => handleComplete(n.id, 'rejected')} style={actionBtnStyle('#dc2626')}>Reject</button>
                        </>
                      ) : n.action_type === 'review' ? (
                        <button onClick={() => handleComplete(n.id)} style={actionBtnStyle('#3b82f6')}>Mark Reviewed</button>
                      ) : n.action_type === 'acknowledge' ? (
                        <button onClick={() => handleComplete(n.id)} style={actionBtnStyle('#3b82f6')}>Acknowledge</button>
                      ) : n.action_type === 'navigate' && n.action_url ? (
                        <button onClick={() => { window.location.hash = n.action_url!; }} style={actionBtnStyle('#3b82f6')}>View</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

function actionBtnStyle(color: string): React.CSSProperties {
  return {
    padding: '4px 10px',
    borderRadius: '4px',
    border: `1px solid ${color}`,
    background: `${color}15`,
    color,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
  };
}

export default ActionQueue;
